import React, { useEffect, useMemo } from 'react';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import ElectronicSignatureFields from '../../../Base/Components/ElectronicSignatureFields/ElectronicSignatureFields';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import useElectronicSignature from '../../Hooks/useElectronicSignature';
import ECardFolderSelectField from '../../../Base/Components/Fields/ECardFolder/ECardFolderSelectField';
import FormCondition from '../../../../Components/Form/FormCondition';
import FormContainer from '../../../../Components/Form/FormContainer';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import useTranslation from '../../../../Hooks/useTranslation';

export interface ElectronicSignatureParamsFormValues {
	electronicSignatureTimestamp?: boolean;
	addToPatientECard?: boolean;
	targetFolder?: SelectValue;
}

export interface ElectronicSignatureParams {
	electronicSignatureTimestamp?: boolean;
	addToPatientECard?: boolean;
	targetFolderId?: string;
}

interface Props {
	onSign: (values: ElectronicSignatureParams) => void;
	userId: string;
}

const ElectronicSignatureRequest: React.FC<Props> = ({ onSign, userId }) => {
	const { isElectronicSignatureTimestampAllowed } = useElectronicSignature();
	const { t } = useTranslation();

	const validationSchema = yup.object({
		electronicSignatureTimestamp: yup.boolean(),
		addToPatientECard: yup.boolean(),
		targetFolder: yup.object().when('addToPatientECard', {
			is: true,
			then: yup.object().shape({
				value: yup
					.string()
					.uuid()
					.required(t('validation.electronicSignature.targetFolder')),
			}),
		}),
	});

	const formMethods = useForm({
		resolver: yupResolver(validationSchema),
		defaultValues: {
			electronicSignatureTimestamp: false,
			addToPatientECard: true,
		},
	});

	useEffect(() => {
		formMethods.reset({
			electronicSignatureTimestamp: isElectronicSignatureTimestampAllowed,
			addToPatientECard: true,
		});
	}, [isElectronicSignatureTimestampAllowed]);

	const handleOnSubmit = (values: ElectronicSignatureParamsFormValues) => {
		const { targetFolder, ...restOfValues } = values;
		onSign({
			targetFolderId: targetFolder?.value as string | undefined,
			...restOfValues,
		});
	};

	return (
		<FormWrapper formMethods={formMethods}>
			<ElectronicSignatureFields showElectronicSignatureField={false} />
			<CheckboxField
				name='addToPatientECard'
				tx='component.electronicSignatureRequest.addToPatientECard'
				isRequired
				isWrapped
			/>
			<FormCondition controlName='addToPatientECard' equal={true}>
				<FormContainer settings={{ showLine: true }}>
					<ECardFolderSelectField
						patientId={userId}
						name='targetFolder'
						label='component.electronicSignatureRequest.targetFolderId'
						isRequired
					/>
				</FormContainer>
			</FormCondition>
			<div className='mt-3'>
				<SubmitButton
					onSubmit={values =>
						handleOnSubmit(values as ElectronicSignatureParamsFormValues)
					}
					title='base.shared.confirm'
				/>
			</div>
		</FormWrapper>
	);
};

export default ElectronicSignatureRequest;
